import React, { useRef, useState, useMemo } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  IconButton
} from '@chakra-ui/react'
import { useSelector } from 'react-redux'

import * as icons from '@heroicons/react/24/outline'
import { capabilities } from '@homevest/utils'
import { useUpupDashboardsQuery, useUpupDeleteDashboardMutation } from 'graphql/generated'
import BasicPageLayout from 'components/TailwindUIToolkit/Layouts/BasicPageLayout'
import DashboardModal, { DashboardRecord } from './AddDashboard'
import { hasCapability } from 'lib/admin-perms'
import { StoreState } from 'store'

const STREAMLIT_CATEGORY = 'Streamlit'

const Dashboards = () => {
  const [modalState, setModalState] = useState<
    { mode: 'new' } | { mode: 'edit'; dashboard: DashboardRecord } | null
  >(null)
  const [dashboardToDelete, setDashboardToDelete] = useState<DashboardRecord | null>(null)
  const cancelDeleteRef = useRef<HTMLButtonElement>(null)
  const admin = useSelector<StoreState, any>((s) => s.admin)
  const [{ data: dashboardsData }, refetchDashboards] = useUpupDashboardsQuery()
  const [{ fetching: isDeletingDashboard }, deleteDashboard] = useUpupDeleteDashboardMutation()
  const canEditDashboards = hasCapability(admin, capabilities.CAPABILITY_TYPES.DASHBOARD_EDITOR)

  // Group dashboards by category
  const groupedDashboards = useMemo(() => {
    if (!dashboardsData?.dashboards) return {}

    const groups = dashboardsData.dashboards.reduce((acc, dashboard) => {
      const category = dashboard.category || 'uncategorized'
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(dashboard)
      return acc
    }, {} as Record<string, typeof dashboardsData.dashboards>)

    // Sort list of dashboards so it renders in a consistent order
    return Object.fromEntries(
      Object.entries(groups)
        .sort(([categoryA, _], [categoryB, __]) => {
          // Always keep streamlit dashboards at top of page
          if (categoryA === STREAMLIT_CATEGORY) {
            return -1
          }
          if (categoryB === STREAMLIT_CATEGORY) {
            return 1
          }
          return categoryA.localeCompare(categoryB)
        })
        // Within each category, sort by dashboard name
        .map(([category, dashboardList]) => [
          category,
          dashboardList.sort((a, b) => a.name.localeCompare(b.name))
        ])
    )
  }, [dashboardsData])

  return (
    <BasicPageLayout
      heading='Dashboards'
      tabTitle='Dashboards'
      topRightElement={
        <Button onClick={() => setModalState({ mode: 'new' })} size='xs' colorScheme='teal'>
          + Dashboard
        </Button>
      }
    >
      <div className='p-4'>
        {Object.entries(groupedDashboards).map(([section, dashboardList]) => (
          <div key={section} className='mb-8'>
            <h2 className='mb-4 text-lg font-semibold capitalize'>{section}</h2>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
              {dashboardList.map((dashboard) => {
                const IconComponent = dashboard.icon ? (icons as any)[dashboard.icon] : null
                return (
                  <div
                    key={dashboard.id}
                    className='group relative rounded-lg bg-white p-4 pr-16 shadow-md'
                  >
                    <a
                      href={dashboard.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex items-center gap-2'
                    >
                      {IconComponent && <IconComponent className='h-4 w-4' />}
                      <h3 className='font-medium'>{dashboard.name}</h3>
                    </a>

                    {canEditDashboards && (
                      <HStack
                        position='absolute'
                        right={2}
                        top='50%'
                        transform='translateY(-50%)'
                        spacing={1}
                      >
                        <IconButton
                          aria-label={`Edit ${dashboard.name}`}
                          icon={<icons.PencilSquareIcon className='h-4 w-4' />}
                          size='xs'
                          variant='ghost'
                          onClick={() => setModalState({ mode: 'edit', dashboard })}
                        />
                        <IconButton
                          aria-label={`Delete ${dashboard.name}`}
                          colorScheme='red'
                          icon={<icons.TrashIcon className='h-4 w-4' />}
                          size='xs'
                          variant='ghost'
                          onClick={() => setDashboardToDelete(dashboard)}
                        />
                      </HStack>
                    )}

                    <div className='pointer-events-none absolute bottom-full left-1/2 mb-2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-gray-100 px-2 py-1 text-xs text-gray-800 group-hover:block'>
                      {dashboard.description}
                      <div className='absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-100'></div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
      <DashboardModal
        isOpen={!!modalState}
        dashboard={modalState?.mode === 'edit' ? modalState.dashboard : undefined}
        onClose={() => {
          setModalState(null)
          refetchDashboards()
        }}
      />
      <AlertDialog
        isOpen={!!dashboardToDelete}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => setDashboardToDelete(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              Delete Dashboard
            </AlertDialogHeader>
            <AlertDialogBody>
              Delete dashboard &quot;{dashboardToDelete?.name}&quot;?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={() => setDashboardToDelete(null)}>
                Cancel
              </Button>
              <Button
                colorScheme='red'
                isLoading={isDeletingDashboard}
                ml={3}
                onClick={async () => {
                  if (!dashboardToDelete) return
                  await deleteDashboard({ id: dashboardToDelete.id })
                  setDashboardToDelete(null)
                  refetchDashboards()
                }}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </BasicPageLayout>
  )
}

export default Dashboards
